import { zodResolver } from "@hookform/resolvers/zod";
import { Spinner } from "@/components/ui/spinner";
import { useForm } from "react-hook-form";
import { ArrowRight, Megaphone, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/react";
import posthog from "posthog-js";
import { toast } from "sonner";
import { useUsageLimit } from "@/hooks/UsageLimitContext";
import { currencySymbol } from "@/lib/format";
import { useDashboardStore } from "@/stores";
import { usePaywallComplete } from "@/hooks/onboarding";
import CampaignFormStep1 from "./CampaignFormStep1";
import CampaignFormStep2 from "./CampaignFormStep2";
import CampaignFormStep3 from "./CampaignFormStep3";
import CampaignFormStep4 from "./CampaignFormStep4";
import {
  CampaignFormData,
  TierComponent,
  formSchema,
} from "./campaignFormTypes";
import { AnimatedSteps } from "./AnimatedStep";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/** Auto-generate description for non-CPA component types at submit time.
 * Persisted to the DB and re-displayed forever — bake the campaign's symbol. */
function generateComponentDescription(comp: TierComponent, currency: string): string {
  const symbol = currencySymbol(currency);
  switch (comp.type) {
    case "max_budget":
      return `Fixed rate up to ${symbol}${comp.value} per creator`;
    case "max_cpm":
      return `Up to ${symbol}${comp.value} CPM`;
    case "custom_formula":
      return `Dynamic rate calculated via formula: ${comp.custom_formula?.expression ?? ""}`;
    case "revenue_share":
      return `${comp.value}% revenue share on sales`;
    case "performance_cpm": {
      // Mirror of amy/lib/domain/performance.ts describePerformanceConfig —
      // the generic (unresolved) sentence; per-creator offers resolve median
      // multiples into exact view counts at negotiation time.
      const cfg = comp.performance_cpm;
      if (!cfg) return "";
      const fmt = (n: number) => n.toLocaleString("en-US");
      const floor = (above: number) =>
        cfg.boundary_mode === "median_multiple"
          ? `${fmt(above)}× the creator's median views`
          : `${fmt(above)} views`;
      const parts = cfg.rates.map((r, i) => {
        const next = cfg.rates[i + 1];
        const span = next
          ? `between ${floor(r.above)} and ${floor(next.above)}`
          : r.above > 0
            ? `above ${floor(r.above)}`
            : "on all views";
        return `${symbol}${fmt(r.cpm)} CPM ${span}`;
      });
      const months = cfg.window_months === 1 ? "1 month" : `${cfg.window_months} months`;
      const capClause =
        cfg.cap_mode === "median_cpm"
          ? `capped per creator at a ${symbol}${fmt(cfg.cap)} CPM on their median views`
          : `capped at ${symbol}${fmt(cfg.cap)} per creator`;
      const flatClause =
        typeof cfg.flat_share === "number" && cfg.flat_share > 0
          ? `, with ${Math.round(cfg.flat_share * 100)}% of the cap guaranteed as a minimum`
          : "";
      return `Performance-based: ${parts.join(", plus ")}, measured ${months} after posting, ${capClause}${flatClause}.`;
    }
    case "cpa":
      return comp.description ?? "";
    case "other":
      return comp.description ?? "";
    default:
      return "";
  }
}

export default function CampaignForm() {
  const [loadingCreate, setLoadingCreate] = useState(false);
  const navigate = useNavigate();
  const { userId, orgId, getToken } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const { showLimitAlert } = useUsageLimit();
  const { planName } = usePaywallComplete();

  const { usage, usageLoading, resetStore } = useDashboardStore();

  useEffect(() => {
    if (usage?.campaigns?.current >= usage?.campaigns?.limit && !usageLoading) {
      showLimitAlert({
        feature: "Campaign Creation",
        current: usage?.campaigns?.current,
        limit: usage?.campaigns?.limit,
      });
    }
  }, [usage, usageLoading]);

  const form = useForm<CampaignFormData>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      campaign_name: "",
      follower_range_from: 0,
      follower_range_to: 1000000000,
      number_of_creators: 10,
      number_of_deliverables: 1,
      creator_prompt: "",
      batch_name: "",
      platform: "tiktok",
      currency: "USD",
      countries: ["US"],

      // Partnership tiers (replaces flat partnership fields)
      tiers: [
        {
          order: 1,
          tier_type: "paid",
          operator: null,
          components: [{ type: "max_budget" }],
        },
      ],

      fields: [
        {
          name: "Name",
          type: "string",
          explanation: "",
          enabled: false,
          description:
            "The influencer/creator's full name (first AND last name).",
        },
        {
          name: "Phone",
          type: "string",
          explanation: "",
          enabled: false,
          description:
            "The influencer/creator's phone number including the country code.",
        },
        {
          name: "Address",
          type: "string",
          explanation: "",
          enabled: false,
          description:
            "The influencer/creator's address for shipping products or sending physical materials needed for the campaign.",
        },
        {
          name: "Billing-Info",
          type: "string",
          explanation: "",
          enabled: false,
          description:
            "The influencer/creator's payment details. Only payment methods explicity listed in the context e.g. IBAN, BANK, PAYPAL etc.. Not the actual number.",
        },
      ],
      exclude_past_campaign_creators: false,
      exclude_excluded_creators: true,
      enable_smart_negotiation: false,
      smart_negotiation_percentage: undefined,
      use_creator_native_language: false,
      use_dub: false,
    },
  });

  const validateCurrentStep = async () => {
    let fieldsToValidate: any[] = [];

    if (currentStep === 1) {
      fieldsToValidate = ["campaign_name", "platform", "currency", "tiers"];
    } else if (currentStep === 2) {
      fieldsToValidate = [
        "follower_range_from",
        "follower_range_to",
        "number_of_creators",
        "countries",
        "creator_prompt",
      ];
    } else if (currentStep === 3) {
      // Check if at least one field is enabled
      const fields = form.getValues("fields");
      const hasEnabledField = fields.some((field) => field.enabled === true);

      if (!hasEnabledField) {
        form.setError("fields", {
          type: "manual",
          message: "At least one field must be enabled",
        });
        return false;
      }

      // Check if Billing-Info is enabled and has explanation
      const billingField = fields.find(
        (field) => field.name === "Billing-Info",
      );
      if (billingField?.enabled) {
        if (
          !billingField.explanation ||
          billingField.explanation.length === 0
        ) {
          const billingFieldIndex = fields.findIndex(
            (field) => field.name === "Billing-Info",
          );
          form.setError(`fields.${billingFieldIndex}.explanation`, {
            type: "manual",
            message:
              "Please explain how you pay creators when billing is enabled",
          });
          return false;
        }
      }

      fieldsToValidate = ["fields"];
    } else if (currentStep === 4) {
      fieldsToValidate = [
        "exclude_past_campaign_creators",
        "exclude_excluded_creators",
        "use_creator_native_language",
        "enable_smart_negotiation",
        "external_account_id",
      ];
    }

    const result = await form.trigger(fieldsToValidate);
    return result;
  };

  async function nextStep() {
    const isValid = await validateCurrentStep();
    if (isValid) {
      setCurrentStep((prev) => Math.min(prev + 1, 4));
    }
  }
  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

  async function onSubmit(values: CampaignFormData) {
    setLoadingCreate(true);

    try {
      const token = await getToken();

      // Transform tiers to API format
      const partnershipTiers = values.tiers.map((tier) => ({
        order: tier.order,
        operator: tier.operator,
        components: tier.components.map((comp) => {
          const result: Record<string, any> = {
            type: comp.type,
          };

          if (comp.type === "custom_formula") {
            result.custom_formula = comp.custom_formula;
            result.description = generateComponentDescription(comp, values.currency);
          } else if (comp.type === "performance_cpm") {
            result.performance_cpm = comp.performance_cpm;
            result.description = generateComponentDescription(comp, values.currency);
          } else if (comp.type === "other") {
            result.description = comp.description ?? "";
            result.value = comp.description ?? "";
          } else if (comp.type === "cpa") {
            result.value = comp.value;
            result.description = comp.description ?? "";
          } else {
            result.value = comp.value;
            result.description = generateComponentDescription(comp, values.currency);
          }

          return result;
        }),
      }));

      const batchNames = [
        ...new Set(
          (values.batch_name ?? "")
            .split(",")
            .map((b) => b.trim())
            .filter(Boolean),
        ),
      ];

      let json_package: Record<string, any> = {
        campaign_name: values.campaign_name,
        platform: values.platform,
        youtube_content_type:
          values.platform === "youtube" ? values.youtube_content_type : null,
        follower_range_from: values.follower_range_from,
        follower_range_to: values.follower_range_to,
        number_of_creators: values.number_of_creators,
        number_of_deliverables: values.number_of_deliverables ?? 1,
        countries: values.countries,
        creator_prompt: values.creator_prompt,
        // Staff-only batch scoping; the server re-verifies uplodio_member.
        // Comma-separated input → array (the search worker accepts one name or a list).
        ...(batchNames.length ? { batch_name: batchNames } : {}),
        partnership: { tiers: partnershipTiers },
        currency: values.currency,
        fields: values.fields.filter((field) => field.enabled),
        exclude_past_campaign_creators: values.exclude_past_campaign_creators,
        exclude_excluded_creators: values.exclude_excluded_creators,
        enable_smart_negotiation: values.enable_smart_negotiation,
        smart_negotiation_percentage: values.smart_negotiation_percentage,
        use_creator_native_language: values.use_creator_native_language,
        use_dub: values.use_dub,
        external_account_id: values.external_account_id,
        messaging: values.messaging,
        user_id: userId,
        organization_id: orgId,
        schedule: {
          timezone: "America/New_York",
          start_hour: 12,
          end_hour: 17
        }
      };

      if (planName?.toLowerCase().includes("free")) {
        json_package.number_of_creators = 2;
      }

      const create_campaign_response = await fetch(
        `${API_BASE_URL}/api/campaigns/create`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(json_package),
        },
      );

      if (!create_campaign_response.ok) {
        const errorData = await create_campaign_response.json();

        if (create_campaign_response.status === 429) {
          console.log("Error data:", errorData);
          showLimitAlert({
            feature: "Campaign Creation",
            current: errorData.current,
            limit: errorData.limit,
          });
        } else {
          toast.error(errorData?.error || "Failed to create campaign");
        }
        throw new Error(
          "Error creating campaign: " + create_campaign_response.statusText,
        );
      }

      posthog.capture("Create Campaign", json_package);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      resetStore();
      setLoadingCreate(false);
      navigate("/dashboard/campaigns");
    } catch (error) {
      console.error("Error:", error);
      setLoadingCreate(false);
    }
  }

  return (
    <div className="relative h-full grid grid-cols-[1fr_2.5rem_auto_2.5rem_1fr] grid-rows-[1fr_1px_auto_1px_1fr] bg-background [--pattern-fg:var(--color-gray-950)]/5 dark:[--pattern-fg:var(--color-gray-200)]/5 overflow-auto [scrollbar-color:--alpha(var(--foreground)/20%)_transparent]">
      <div className="col-start-3 row-start-3 flex max-w-2xl min-w-2xl w-full flex-col bg-sidebar p-1">
        <div className="flex flex-row items-center gap-2 p-4 pb-3">
          <h1 className="text-2xl font-semibold">Craft a new campaign</h1>
        </div>
        <div className="border p-6 rounded-xl min-w-full bg-background shadow-xs">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div key="form-fields" className="space-y-6">
                <AnimatedSteps step={currentStep} direction={currentStep}>
                  {currentStep === 1 && <CampaignFormStep1 form={form} />}
                  {currentStep === 2 && <CampaignFormStep2 form={form} usage={usage} />}
                  {currentStep === 3 && <CampaignFormStep3 form={form} loading={loadingCreate} />}
                  {currentStep === 4 && <CampaignFormStep4 form={form} loading={loadingCreate} />}
                </AnimatedSteps>
              </div>

              {/* Navigation Buttons */}
              <div className="flex justify-end space-x-2">
                {currentStep > 1 && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={prevStep}
                    className="group"
                    disabled={loadingCreate}
                  >
                    <ArrowLeft className="group-hover:-translate-x-0.5 transition-all" />
                    Back
                  </Button>
                )}
                {currentStep < 4 && (
                  <Button type="button" onClick={nextStep} className="group">
                    Next{" "}
                    <ArrowRight className="group-hover:translate-x-0.5 transition-all" />
                  </Button>
                )}
                {currentStep === 4 && (
                  <Button
                    type="submit"
                    className="group"
                    disabled={!form.formState.isValid || loadingCreate}
                  >
                    {loadingCreate && <Spinner />}
                    {!loadingCreate && (
                      <Megaphone className="group-hover:-rotate-12 transition-all" />
                    )}
                    Create Campaign
                  </Button>
                )}
              </div>
            </form>
          </Form>
        </div>
      </div>
      <div className="relative -right-px col-start-2 row-span-full row-start-1 border-x border-x-(--pattern-fg) bg-[repeating-linear-gradient(315deg,var(--pattern-fg)_0,var(--pattern-fg)_1px,transparent_0,transparent_50%)] bg-size-[10px_10px] bg-fixed"></div>
      <div className="relative -left-px col-start-4 row-span-full row-start-1 border-x border-x-(--pattern-fg) bg-[repeating-linear-gradient(315deg,var(--pattern-fg)_0,var(--pattern-fg)_1px,transparent_0,transparent_50%)] bg-size-[10px_10px] bg-fixed"></div>
      <div className="relative -bottom-px col-span-full col-start-1 row-start-2 h-px bg-(--pattern-fg)"></div>
      <div className="relative -top-px col-span-full col-start-1 row-start-4 h-px bg-(--pattern-fg)"></div>
    </div>
  );
}
